import { useState } from "react";
import { useAppData } from "@/data/AppDataContext";
import type { Job } from "@/data/types";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Star, MapPin, Wrench, Send, CheckCircle } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: Job;
}

export function SendOpportunityDialog({ open, onOpenChange, job }: Props) {
  const { contractors, opportunities, setOpportunities, setJobs, jobs } = useAppData();
  const [selected, setSelected] = useState<string[]>([]);
  const [sent, setSent] = useState(false);

  const existingContractorIds = opportunities.filter((o) => o.jobId === job.id).map((o) => o.contractorId);
  const available = contractors.filter((c) => c.active && !existingContractorIds.includes(c.id));

  const toggle = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleSend = () => {
    const today = new Date().toISOString().split("T")[0];
    const newOpps = selected.map((cId, idx) => ({
      id: `OPP-NEW-${Date.now()}-${idx}`,
      jobId: job.id,
      contractorId: cId,
      sentDate: today,
      viewedDate: "",
      responseDate: "",
      responseStatus: "Pending" as const,
      bidAmount: null,
      bidNotes: "",
      isWinner: false,
    }));

    setOpportunities((prev) => [...prev, ...newOpps]);

    // Update job status
     setJobs((prev) =>
       prev.map((j) =>
         j.id === job.id
           ? {
               ...j,
               dispatchStatus: "Sent to Contractor Network" as const,
               currentStage: j.currentStage === "Ready for Scheduling" || j.currentStage === "Available for Contractor Assignment"
                 ? "Available for Contractor Assignment" as const
                 : j.currentStage,
               activityHistory: [
                 ...j.activityHistory,
                 {
                   id: `act-${Date.now()}`,
                   date: today,
                   action: `Opportunity sent to ${selected.length} contractor${selected.length > 1 ? "s" : ""}`,
                   user: "Admin",
                 },
               ],
             }
           : j,
       ),
     );

    setSent(true);
    setTimeout(() => {
      setSent(false);
      setSelected([]);
      onOpenChange(false);
    }, 1500);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base">Send Opportunity — {job.id}</DialogTitle>
          <DialogDescription className="text-xs">
            {job.projectType} · {job.scopeSummary} · Payout ${job.payoutAmount.toLocaleString()}
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="flex flex-col items-center gap-2 py-8">
            <CheckCircle className="h-10 w-10 text-success" />
            <p className="text-sm font-semibold">Opportunity Sent!</p>
            <p className="text-xs text-muted-foreground">
              Sent to {selected.length} contractor{selected.length > 1 ? "s" : ""}
            </p>
          </div>
        ) : (
          <>
            {available.length === 0 ? (
              <p className="text-xs text-muted-foreground py-6 text-center">
                All active contractors already have this opportunity.
              </p>
            ) : (
              <div className="space-y-2">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.12em]">
                  Select Contractors ({available.length} available)
                </p>
                <div className="space-y-1.5 max-h-[50vh] overflow-y-auto">
                  {available.map((c) => {
                    const isChecked = selected.includes(c.id);
                    return (
                      <label
                        key={c.id}
                        className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                          isChecked ? "border-primary bg-primary/5" : "border-border hover:bg-muted/30"
                        }`}
                      >
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() => toggle(c.id)}
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{c.companyName}</span>
                              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                                c.availabilityStatus === "Available"
                                  ? "bg-success/15 text-success"
                                  : c.availabilityStatus === "Limited"
                                  ? "bg-warning/15 text-warning"
                                  : "bg-destructive/15 text-destructive"
                              }`}>
                                {c.availabilityStatus}
                              </span>
                            </div>
                            <div className="flex items-center gap-0.5 shrink-0">
                              {[1, 2, 3, 4, 5].map((s) => (
                                <Star
                                  key={s}
                                  className={`h-2.5 w-2.5 ${s <= Math.round(c.rating) ? "text-warning fill-warning" : "text-muted"}`}
                                />
                              ))}
                              <span className="text-[10px] text-muted-foreground ml-0.5">{c.rating}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground">
                            <span className="inline-flex items-center gap-1"><Wrench className="h-2.5 w-2.5" />{c.tradeType}</span>
                            <span className="inline-flex items-center gap-1"><MapPin className="h-2.5 w-2.5" />{c.serviceArea}</span>
                            {c.maxWeeklyJobs > 0 && (
                              <span className="text-[10px]">{c.maxWeeklyJobs} jobs/wk</span>
                            )}
                          </div>
                          {c.notes && <p className="text-[10px] text-muted-foreground mt-1 italic">{c.notes}</p>}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pt-2 border-t">
              <p className="text-xs text-muted-foreground">
                {selected.length} selected
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button size="sm" disabled={selected.length === 0} onClick={handleSend}>
                  <Send className="h-3.5 w-3.5" />
                  Send to {selected.length || "—"}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
